// frontend/src/components/OwnerDashboard/StatBar.js
// One panel, three cells divided by hairlines — never wraps into an orphan card.

import { IconCar, IconCard, IconClock } from '../icons/Icons';
import { fmt } from '../../theme/iparc';

const Cell = ({ label, icon, children }) => (
  <div className="bg-gradient-to-b from-[#17140F] to-[#131110] px-[18px] py-4">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] whitespace-nowrap">{label}</span>
      <span className="text-[#4E4740]">{icon}</span>
    </div>
    {children}
  </div>
);

export default function StatBar({ health, totalCost, monthlyAvg, costPerKm, spendSeries = [] }) {
  const perVehicle = health.count ? Math.round(totalCost / health.count) : 0;

  return (
    <div className="grid gap-px rounded-2xl border border-[#241F1B] bg-[#241F1B] overflow-hidden
                    [grid-template-columns:repeat(auto-fit,minmax(196px,1fr))]">

      <Cell label="Véhicules actifs" icon={<IconCar className="w-[15px] h-[15px]" />}>
        <div className="flex items-end gap-2 mt-3">
          <span className="font-['Space_Grotesk',sans-serif] text-[34px] font-semibold leading-none tracking-[-.03em] tabular-nums text-[#F4F1EA]">
            {health.count}
          </span>
          <span className="text-[11.5px] text-[#74A98D] pb-1 whitespace-nowrap">{health.okCount} conformes</span>
        </div>
        <div className="flex gap-[3px] mt-3.5">
          {health.segments.map((c, i) => (
            <span key={i} className="h-1 flex-1 rounded-[3px]" style={{ background: c }} />
          ))}
        </div>
        <p className="text-[10.5px] text-[#5C554C] mt-[7px]">{health.splitLabel}</p>
      </Cell>

      <Cell label="Dépenses 12 mois" icon={<IconCard className="w-[15px] h-[15px]" />}>
        <div className="flex items-end gap-1.5 mt-3">
          <span className="font-['Space_Grotesk',sans-serif] text-[29px] font-semibold leading-none tracking-[-.03em] tabular-nums whitespace-nowrap text-[#F4F1EA]">
            {fmt(totalCost)}
          </span>
          <span className="text-[13px] text-[#8F877B] pb-0.5">F</span>
        </div>
        <Sparkline series={spendSeries} />
        <p className="text-[10.5px] text-[#5C554C] mt-1.5 tabular-nums leading-[1.45]">
          Moyenne {fmt(monthlyAvg)} F / mois<br />{fmt(perVehicle)} F par véhicule
        </p>
      </Cell>

      <Cell label="Coût au km" icon={<IconClock className="w-[15px] h-[15px]" />}>
        <div className="flex items-end gap-1.5 mt-3">
          <span className="font-['Space_Grotesk',sans-serif] text-[34px] font-semibold leading-none tracking-[-.03em] tabular-nums text-[#F4F1EA]">
            {costPerKm.total}
          </span>
          <span className="text-[13px] text-[#8F877B] pb-0.5">F/km</span>
        </div>
        <div className="flex flex-col gap-[7px] mt-3.5">
          {costPerKm.parts.length === 0 && (
            <p className="text-[10.5px] text-[#5C554C] leading-[1.45]">
              {costPerKm.total === 0
                ? 'Renseignez le kilométrage pour obtenir le coût au km.'
                : 'Détail indisponible — aucun coût pièces/main-d’œuvre enregistré.'}
            </p>
          )}
          {costPerKm.parts.map((p) => (
            <div key={p.label} className="flex items-center gap-2.5">
              <span className="text-[10.5px] text-[#8F877B] w-16 shrink-0">{p.label}</span>
              <span className="flex-1 h-[5px] rounded-[3px] bg-[#241F1B] overflow-hidden">
                <span className="block h-full rounded-[3px]" style={{ width: p.pct, background: p.color }} />
              </span>
              <span className="text-[10.5px] text-[#8F877B] tabular-nums">{p.value} F</span>
            </div>
          ))}
        </div>
      </Cell>
    </div>
  );
}

function Sparkline({ series }) {
  if (series.length < 2) return <div className="h-[34px] mt-3" />;
  const max = Math.max(...series, 1);
  const step = 220 / (series.length - 1);
  const pts = series.map((v, i) => `${(i * step).toFixed(1)} ${(30 - (v / max) * 26).toFixed(1)}`);
  return (
    <svg viewBox="0 0 220 34" preserveAspectRatio="none" className="w-full h-[34px] mt-3 block">
      <defs>
        <linearGradient id="spk" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#C2BFB6" stopOpacity=".22" />
          <stop offset="1" stopColor="#C2BFB6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M${pts.join(' L')} L220 34 L0 34 Z`} fill="url(#spk)" />
      <path d={`M${pts.join(' L')}`} fill="none" stroke="#C2BFB6" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
